/**
 * 機能 2: 最寄り店舗検索
 *
 * 「現在地でさがす」ボタンをクリック → Geolocation API で現在地取得
 * → ハバーサイン公式で最寄り店舗を特定 → エリア・店舗 select に自動反映
 *
 * また、URL に lat / lng の GET パラメータが付与されている場合
 * （店舗一覧ページの「現在地でさがす」リンクからの遷移）は、
 * ページ表示時に同じロジックで最寄り店舗を自動選択する。
 */

import {
    GEOLOCATION_OPTIONS,
    buildGeolocationErrorMessage,
    haversineDistanceKm,
    setSelectValue,
} from "./utils.js";

/**
 * 現在地から最寄り店舗の ID を特定する。
 *
 * shopAreaMap が渡されている場合、エリア未設定の店舗は候補から除外する。
 * エリア select と店舗 select を必ずペアで反映するため
 * （エリア無し店舗を選ぶとエリアが未選択のままになり、
 *   店舗 select も disabled のままになるのを防ぐ）。
 *
 * @param {number} lat - 現在地の緯度
 * @param {number} lng - 現在地の経度
 * @param {import('./utils.js').ShopGeoMap} shopGeoMap - 店舗 ID → 緯度経度
 * @param {import('./utils.js').ShopAreaMap} shopAreaMap - 店舗 ID → エリア value
 * @returns {string|null} 最寄り店舗 ID。有効な店舗が無い場合は null
 */
const findNearestShopId = (lat, lng, shopGeoMap, shopAreaMap) => {
    // 対応表が空の場合はエリア連動機能自体が無効のため、全店舗を候補にする
    const requiresArea = Object.keys(shopAreaMap).length > 0;

    let nearestId = null;
    let nearestDistance = Infinity;

    Object.keys(shopGeoMap).forEach((id) => {
        const geo = shopGeoMap[id];
        // 不正なデータ（型不一致 / null）はスキップ
        if (!geo || typeof geo.lat !== "number" || typeof geo.lng !== "number") return;

        // エリア未設定の店舗はスキップ
        const areaValue = shopAreaMap[id];
        if (requiresArea && (areaValue === undefined || areaValue === null || areaValue === "")) return;

        const distance = haversineDistanceKm(lat, lng, geo.lat, geo.lng);
        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestId = id;
        }
    });

    return nearestId;
};

/**
 * 「現在地でさがす」ボタンの初期化。
 * ボタンが存在しない画面では何もしない。
 *
 * @param {Object} params
 * @param {HTMLElement} params.root - ルート要素（.c-forms-row-area-shop）
 * @param {HTMLSelectElement} params.areaNative - エリア select
 * @param {HTMLSelectElement} params.shopNative - 店舗 select
 * @param {import('./utils.js').ShopAreaMap} params.shopAreaMap - 店舗 ID → エリア value
 * @param {import('./utils.js').ShopGeoMap} params.shopGeoMap - 店舗 ID → 緯度経度
 * @returns {void}
 */
export const setupNearestShopFinder = ({ root, areaNative, shopNative, shopAreaMap, shopGeoMap }) => {
    /**
     * 現在地から最寄り店舗を特定し、エリア → 店舗 ID の順で select に反映する。
     *
     * エリアを先に設定する理由:
     *   shopNative に値を入れる前に setupAreaShopFilter のエリア change リスナーで
     *   店舗フィルタを更新させ、対象店舗が hidden / disabled にならない状態にしてから
     *   店舗 ID を反映する必要があるため。
     *
     * @param {number} lat - 現在地の緯度
     * @param {number} lng - 現在地の経度
     * @param {boolean} [silent=false] - true の場合、店舗を特定できなくても alert を出さない（URL パラメータからの自動選択用）
     */
    const applyNearestShop = (lat, lng, silent = false) => {
        // 緯度経度が 1 件も登録されていなければ通知して終了
        if (Object.keys(shopGeoMap).length === 0) {
            if (!silent) window.alert("店舗の位置情報が登録されていません。");
            return;
        }

        const nearestId = findNearestShopId(lat, lng, shopGeoMap, shopAreaMap);

        // 有効な店舗が 1 件も無い場合（全件 lat/lng が不正等）
        if (nearestId === null) {
            if (!silent) window.alert("店舗の位置情報が登録されていません。");
            return;
        }

        // エリア → 店舗の順で select に反映
        applyShopSelection(nearestId);
    };

    /**
     * 指定した店舗 ID をエリア → 店舗の順で select に反映する。
     *
     * @param {string|number} shopId - 店舗 ID
     */
    const applyShopSelection = (shopId) => {
        const areaValue = shopAreaMap[shopId];
        if (areaValue !== undefined && areaValue !== null && areaValue !== "") {
            setSelectValue(areaNative, areaValue);
        }
        setSelectValue(shopNative, shopId);
    };

    // ── URL パラメータからの自動選択 ──
    const params = new URLSearchParams(window.location.search);

    // shop_id: 店舗一覧ページの地図モーダル（店舗カード）経由で店舗を直接指定して遷移した場合
    const paramShopId = params.get("shop_id");
    const hasShopOption = (id) =>
        Array.from(shopNative.options).some((opt) => opt.value === String(id));
    if (paramShopId && hasShopOption(paramShopId)) {
        applyShopSelection(paramShopId);
    } else {
        // lat / lng: 店舗一覧ページの「現在地でさがす」リンク経由で遷移した場合に最寄り店舗を反映する
        const paramLat = Number.parseFloat(params.get("lat") ?? "");
        const paramLng = Number.parseFloat(params.get("lng") ?? "");
        if (Number.isFinite(paramLat) && Number.isFinite(paramLng)) {
            applyNearestShop(paramLat, paramLng, true);
        }
    }

    // ボタンが無い画面では何もしない
    const findButton = root.querySelector("[data-find-nearest-shop]");
    if (!findButton) return;

    // ── ボタンクリックハンドラ ──
    findButton.addEventListener("click", (e) => {
        // a タグ / submit type の暴発防止
        e.preventDefault();

        // 非対応ブラウザのフォールバック
        if (!("geolocation" in navigator)) {
            window.alert("お使いのブラウザは位置情報の取得に対応していません。");
            return;
        }

        // 取得中の二重クリック防止
        findButton.setAttribute("disabled", "");

        navigator.geolocation.getCurrentPosition(
            // 成功コールバック
            (position) => {
                findButton.removeAttribute("disabled");
                applyNearestShop(position.coords.latitude, position.coords.longitude);
            },
            // 失敗コールバック（権限拒否 / タイムアウト / 取得不可）
            (error) => {
                findButton.removeAttribute("disabled");
                window.alert(buildGeolocationErrorMessage(error));
            },
            GEOLOCATION_OPTIONS,
        );
    });
};
